import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { CheckBoxIcon, WindowCloseSolidIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { PremiumStores } from '@/services/premiumStores/premiumStores.types';
import StorePreview from '../StorePreview';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description?: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel?: () => Promise<void>;
}) => void;

export function getColumns(
  approveStoreInfo: (holdId: string) => Promise<unknown>,
  rejectStoreInfo: (holdId: string) => Promise<unknown>,
  openConfirmation: OpenConfirmationProps,
  t: ReturnType<typeof useDynamicLocale>['t'],
  canApproveClub: boolean,
  canRejectClub: boolean,
): ColumnDef<PremiumStores>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-primary truncate max-w-[10rem]">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'storeNameEng',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeInfo')} />,
      cell: ({ row }) => {
        const storeName = row.original.storeName;
        const storeNameEng = row.original.storeNameEng;
        const storeImage = row.original.clubLogo;

        return (
          <Can
            rule={{ allOf: [PERMS.club.premium.editView] }}
            fallback={
              <div className="flex items-center space-x-3 max-w-[20rem] truncate ">
                <img
                  src={storeImage?.path}
                  alt={storeImage?.alt ?? ''}
                  className="w-8 shrink-0 h-8 rounded-full object-cover"
                />
                <div className="truncate">
                  <div className="font-medium text-caption text-foreground-subtle underline">{storeNameEng}</div>
                  <div className="text-foreground-subtle font-medium text-caption">{storeName}</div>
                </div>
              </div>
            }
          >
            <Button
              variant={'ghost'}
              className="flex items-center space-x-3 max-w-[20rem] truncate "
              onClick={() => {
                openConfirmation({
                  actionLabel: canApproveClub ? t('common.page.button.approve') : '',
                  cancelLabel: canRejectClub ? t('common.page.button.reject') : '',
                  size: 'lg',
                  onCancel: async () => {
                    await rejectStoreInfo(row.original.holdId);
                  },
                  onAction: async () => {
                    await approveStoreInfo(row.original.holdId);
                  },
                  title: 'Store Preview',

                  content: <StorePreview clubId={row.original.clubId} holdId={row.original.holdId} />,
                });
              }}
            >
              <img
                src={storeImage?.path}
                alt={storeImage?.alt ?? ''}
                className="w-8 shrink-0 h-8 rounded-full object-cover"
              />
              <div className="truncate">
                <div className="font-medium text-caption text-link underline">{storeNameEng}</div>
                <div className="text-foreground-subtle font-medium text-caption">{storeName}</div>
              </div>
            </Button>
          </Can>
        );
      },
      meta: {
        label: 'Store Info',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'telephone',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.telephone')} />,
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('telephone')}</div>,
      meta: {
        label: 'Telephone',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'emailAddress',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.emailAddress')} />
      ),
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('emailAddress')}</div>,
      meta: {
        label: 'Email Address',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'prefecture',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.location')} />,
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('prefecture')}</div>,
      meta: {
        label: 'Prefecture',
      },
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption text-primary whitespace-nowrap">
            {formatDate(Number(row.getValue('createdDate')), 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption text-primary whitespace-nowrap">
            {row.getValue('updatedDate') ? formatDate(Number(row.getValue('updatedDate')), 'YYYY/MM/DD HH:mm:ss') : '-'}
          </div>
        );
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      meta: {
        label: 'Action',
      },
      id: 'actions',

      cell: ({ row }) => (
        <div className="flex items-center gap-4">
          <Can rule={{ allOf: [PERMS.club.premium.approve] }}>
            <CheckBoxIcon
              className="text-status-success cursor-pointer"
              height={16}
              width={16}
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description: t('common.page.modal.approveStoreMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  size: 'lg',
                  actionLabel: t('common.page.modal.confirm'),
                  onAction: async () => {
                    await approveStoreInfo(row.original.holdId);
                  },
                  variant: 'warning',
                });
              }}
            />
          </Can>
          <Can rule={{ allOf: [PERMS.club.premium.reject] }}>
            <WindowCloseSolidIcon
              className="text-status-error cursor-pointer"
              height={16}
              width={16}
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description: t('common.page.modal.rejectStoreMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  size: 'lg',
                  actionLabel: t('common.page.modal.confirm'),
                  onAction: async () => {
                    await rejectStoreInfo(row.original.holdId);
                  },
                  variant: 'warning',
                });
              }}
            />
          </Can>
        </div>
      ),
      size: 40,
    },
  ];
}
